import { appContext } from '../../../../AppContext';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import { DataHubCredentials, DataHubPublisher } from './DataModels';
import Helper from '../../../../Common/Helper';
import { PagedResponse } from "@iconics/core";
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { IRouteParams } from '../Edit/EditDataHubPublisher';
import { IDataHubCredentials } from '../../DataHubCredentials/List/ListDataHubCredentials';
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons } from '@iconics/components';

class ListDataHubPublisher extends IbssPage<IProps, IState>
{
    private get local() { return appContext().localStorageProvider; }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            canCreate: this.local.hasRight('DATAMODEL.Tenancy.Create'),  // displayName: TODO proper premission does not exist yet
            dataHubPublishersGrid: { rows: [] },
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = appContext().labels.Platform_DataHubPublisher_PageTitle;
    }

    private dataHubCredentials?: IDataHubCredentials[];
    private async getDataHubCredentials(): Promise<IDataHubCredentials[]>
    {
        if (!this.dataHubCredentials)
        {
            const dataHubCredentialsData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.get<PagedResponse<DataHubCredentials[]>>({
                select: DataHubCredentials,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });

            this.dataHubCredentials = dataHubCredentialsData.value.map(x => ({
                id: x.Id,
                name: x.Name,
                eTag: x['@odata.etag'],
                accessKey: x.AccessKey,
                connectionString: x.ConnectionString,
            }));
        }
        return this.dataHubCredentials;
    }

    private async handleRowsRequest(request: IGridRowsRequest<IDataHubPublisher>): Promise<IGridRowsResponse<IDataHubPublisher>>
    {
        const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubPublisher.get<PagedResponse<DataHubPublisher[]>>({
            select: DataHubPublisher,
            tenancy: appContext().tenantId,
            skipToken: request.skipToken,
            top: request.take,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const dataHubCredentials = await this.getDataHubCredentials();

        const dataHubPublishers = response.value.map(x => ({
            id: x.Id.toString(),
            name: x.Name,
            eTag: x['@odata.etag'],
            dataHubCredentialId: x.ForwarderEventHubID,
            dataHubCredentialName: dataHubCredentials.find(y => y.id === x.ForwarderEventHubID)?.name ?? '',
            moduleConfigurationId: x.ModuleConfigurationEntityId,
            publishingType: x.ForwarderType ?? '',
        }));

        return { rows: dataHubPublishers, nextSkipToken: response.skipToken };
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            title={labels.Platform_DataHubPublisher_ListTitle}
                            subTitle={labels.Platform_DataHubPublisher_ListSubTitle}
                            icon={<SerendieSymbol name='arrow-left-circle' />}
                        >
                            <GridContainer>
                                <GridItem>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.props.navigate(`/admin/data-hub-publishers/new`)}
                                                disabled={!this.state.canCreate}
                                            >
                                                {labels.Platform_CommonAction_Add}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<DataHubPublisher>("Name"),
                                                title: labels.DataHubPublisher_Name,
                                                getCellValue: i => i.name,
                                                columnWidth: 50,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IDataHubPublisher>("dataHubCredentialName"),
                                                title: labels.DataHubPublisher_DataHubCredential_Id,
                                                getCellValue: i => i.dataHubCredentialName,
                                                columnWidth: 50,
                                            },
                                        ]}
                                        data={this.state.dataHubPublishersGrid}
                                        onChange={i => this.setState({ dataHubPublishersGrid: i })}
                                        rowId={i => i.id}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        onCellClicked={i => this.props.navigate(`/admin/data-hub-publishers/${i.id}/edit`)}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
            </>
        );
    }
}

export default withRouting(ListDataHubPublisher);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IState
{
    canCreate: boolean;
    dataHubPublishersGrid: IGridData<IDataHubPublisher>;
}

export interface IDataHubPublisher
{
    id: string;
    name: string;
    dataHubCredentialId: number;
    dataHubCredentialName: string;
    moduleConfigurationId: string;
    publishingType: string;
    eTag: string;
}